import React, { useRef, useState, useEffect } from "react";
import { CheckCircle, XCircle, FileText, FileDown } from "lucide-react";
import { toast } from "sonner";
import useApi from "../../../hook/req";
import useRequest from "../../../hook/use-req";
import * as XLSX from 'xlsx';
import { SingleAudienceResponse } from "../../../../views/marketing-pages/campaign/types";
import { Audience } from "../../../../views/marketing-pages/create-campaign/types";


const UPLOAD_STATUS_POLL_INTERVAL_MS = 2000;
const UPLOAD_STATUS_MAX_ATTEMPTS = 30; // ~60s before giving up

// The status-poll response wraps the actual job info under a `job` key
// (per /audience/upload/status/{job_id} — see the async-upload API docs);
// a couple of other shapes (`data`, `result`) are unwrapped too as a
// defensive fallback in case a different deployment nests it differently.
const unwrapPayload = (obj: any): any => {
  if (!obj || typeof obj !== "object") return obj;
  return obj.job ?? obj.data ?? obj.result ?? obj;
};

const extractAudienceId = (obj: any): string | undefined => {
  const o = unwrapPayload(obj);
  return o?.audience_id || o?.id || o?.audienceId || undefined;
};

const extractJobId = (obj: any): string | undefined => {
  const o = unwrapPayload(obj);
  return o?.job_id || o?.jobId || o?.task_id || o?.taskId || undefined;
};

const extractStatusState = (obj: any): string => {
  const o = unwrapPayload(obj);
  return String(o?.status ?? o?.job_status ?? o?.state ?? o?.job_state ?? "").toLowerCase();
};

const DONE_STATES = ["completed", "complete", "done", "success", "succeeded", "finished", "ready"];
const FAILED_STATES = ["failed", "error", "errored", "cancelled", "canceled", "rejected"];

interface UploadTabProps {
  channel: string;
  onUploadSuccess: (
    audienceId: string,
    fileName: string,
    contacts: number,
    csvColumns?: string[],
    first_row_values?: string[],
    metadata?: Record<string, any>,
    csvPreviewRow?: Record<string, string>
  ) => void;
  audienceById: SingleAudienceResponse | null;
  onValidation: (isValid: boolean) => void;
  // Audiences already picked via the Segments/Manual tabs — shown as
  // removable chips above the dropzone so switching tabs doesn't hide what's
  // already selected (they all feed the same campaign-wide audience list).
  selectedAudiences?: Audience[];
  onRemoveAudience?: (audienceId: string) => void;
}

interface UploadResults {
  fileName: string;
  totalRecords: number;
  validRecords: number;
  invalidRecords: number;
  duplicateRecords: number;
  // Only meaningful for email — contacts who unsubscribed from this list.
  unsubscribedRecords: number;
  csvColumns?: string[];
  csvPreviewRow?: Record<string, string>;
  preview?: Array<{ sno: number; contact: string; status: string }>;
}

const ACCEPTED_EXTENSIONS = [".csv", ".xlsx", ".xls"];

const UploadTab: React.FC<UploadTabProps> = ({
  channel,
  onUploadSuccess,
  audienceById,
  onValidation,
  selectedAudiences,
  onRemoveAudience,
}) => {
  const token = localStorage.getItem("authToken");

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadResults, setUploadResults] = useState<UploadResults | null>(null);
  const [hasUploaded, setHasUploaded] = useState(false);
  // Previously discarded via `[, setUploadedAudienceId]` — needed now so
  // "Delete" on the uploaded-file card can tell the parent which audience to
  // drop from the campaign's selection, not just reset this tab's own view.
  const [uploadedAudienceId, setUploadedAudienceId] = useState<string | null>(null);
  const [apiResponseData, setApiResponseData] = useState<any>(null); // Store API response

  const uploadInProgress = useRef(false);
  const uploadFileInputRef = useRef<HTMLInputElement>(null);

  // Backend's current API is the async variant — it hands back a job_id
  // and computes the real result (audience_id + metadata) in the
  // background, so uploadFileImmediately below polls
  // /audience/upload/status/{job_id} via pollUploadStatus until it's done.
  const { makeRequest: uploadFileAudience } = useApi(
    "/audience/upload/csv/async",
    "POST",
    { Authorization: `Bearer ${token}`, "X-AUTH-TYPE": "jwt" }
  );

  const { makeRequest: checkUploadStatus } = useRequest(
    "/audience/upload/status",
    "GET",
    { Authorization: `Bearer ${token}`, "X-AUTH-TYPE": "jwt" }
  );

  // ====================== PREFILL EXISTING AUDIENCE ======================
  useEffect(() => {
    if (!audienceById || audienceById.type === "manual") return;

    const totalRecords = audienceById?.total_count ?? 0;
    if (totalRecords === 0) return;

    let fileName = "audience_file";
    if (audienceById.file_url) {
      const urlParts = audienceById.file_url.split("/");
      fileName = urlParts[urlParts.length - 1] || fileName;
      fileName = fileName.replace(/_\w{8}-\w{4}-\w{4}-\w{4}-\w{12}/, "");
    } else if (audienceById.audience_id) {
      fileName = `audience_${audienceById.audience_id.slice(0, 8)}.csv`;
    }

    // Set results from existing audience (already uploaded)
    const results: UploadResults = {
      fileName,
      totalRecords,
      validRecords: audienceById?.unique_count ?? 0,
      invalidRecords: audienceById?.invalids_count ?? 0,
      duplicateRecords: audienceById?.duplicates_count ?? 0,
      unsubscribedRecords: audienceById?.unsubscribed_count ?? 0,
      preview: [{ sno: 1, contact: "Loaded from previously uploaded file", status: "valid" }],
    };

    setUploadResults(results);
    setUploadedAudienceId(audienceById.audience_id);
    setHasUploaded(true);
    setSelectedFile(null);
    setApiResponseData(audienceById); // Store the existing audience data

    onUploadSuccess(audienceById.audience_id, fileName, audienceById?.unique_count ?? 0);
    onValidation((audienceById?.unique_count ?? 0) > 0);
  }, [audienceById]);


  const parseCSV = (text: string): string[] => {
    const lines = text.split("\n");
    const firstLine = lines[0]?.trim().toLowerCase() || "";
    const isHeader = /phone|email|contact|number/.test(firstLine);
    const startIndex = isHeader ? 1 : 0;
    const contacts: string[] = [];
    for (let i = startIndex; i < lines.length; i++) {
      const firstCol = lines[i].split(",")[0]?.trim();
      if (firstCol) contacts.push(firstCol);
    }
    return contacts;
  };

  const parseXLSX = (file: File): Promise<string[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: "array" });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as any[][];
          const firstRow = rows[0] || [];
          const isHeader = firstRow.some((cell) =>
            typeof cell === "string" && /phone|email|contact|number/i.test(cell)
          );
          const startIndex = isHeader ? 1 : 0;
          const contacts: string[] = [];
          for (let i = startIndex; i < rows.length; i++) {
            const cell = rows[i]?.[0];
            if (cell) contacts.push(String(cell).trim());
          }
          resolve(contacts);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsArrayBuffer(file);
    });
  };

  // add alongside parseCSV
  const parseCSVHeaders = (text: string): { headers: string[]; firstRow: Record<string, string> } => {
    const lines = text.split("\n").filter(Boolean);
    const headers = lines[0]?.split(",").map((h) => h.trim()) || [];
    const firstRowValues = lines[1]?.split(",").map((v) => v.trim()) || [];
    const firstRow: Record<string, string> = {};
    headers.forEach((h, i) => (firstRow[h] = firstRowValues[i] || ""));
    return { headers, firstRow };
  };

  const parseXLSXHeaders = (file: File): Promise<{ headers: string[]; firstRow: Record<string, string> }> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: "array" });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as any[][];
          const headers = (rows[0] || []).map((h) => String(h).trim());
          const firstRowValues = rows[1] || [];
          const firstRow: Record<string, string> = {};
          headers.forEach((h, i) => (firstRow[h] = String(firstRowValues[i] ?? "")));
          resolve({ headers, firstRow });
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsArrayBuffer(file);
    });
  };

  const processFile = async (file: File) => {
    setIsParsing(true);
    setUploadError(null);

    try {
      const ext = "." + file.name.split(".").pop()?.toLowerCase();
      let contacts: string[] = [];
      let headers: string[] = [];
      let firstRow: Record<string, string> = {};

      if (ext === ".csv") {
        const text = await file.text();
        contacts = parseCSV(text);
        ({ headers, firstRow } = parseCSVHeaders(text));
      } else if (ext === ".xlsx" || ext === ".xls") {
        contacts = await parseXLSX(file);
        ({ headers, firstRow } = await parseXLSXHeaders(file));
      } else throw new Error("Unsupported file format");

      if (contacts.length === 0) throw new Error("No contacts found in file");

      const parsedData = { contacts, fileName: file.name, headers, firstRow };
      setTimeout(() => uploadFileImmediately(file, parsedData), 400);
    } catch (error: any) {
      setUploadError(error.message || "Failed to parse file");
      setIsParsing(false);
    }
  };

  const validateFile = (file: File): boolean => {
    const ext = "." + file.name.split(".").pop()?.toLowerCase();
    if (!ACCEPTED_EXTENSIONS.includes(ext)) {
      setUploadError("Only CSV or XLSX files are accepted.");
      return false;
    }
    if (file.size === 0) {
      setUploadError("File is empty.");
      return false;
    }
    return true;
  };

  const resetUploadState = () => {
    setSelectedFile(null);
    setUploadResults(null);
    setUploadError(null);
    setUploadProgress(0);
    setIsProcessing(false);
    setIsParsing(false);
    setHasUploaded(false);
    setUploadedAudienceId(null);
    setApiResponseData(null);
    uploadInProgress.current = false;
    if (uploadFileInputRef.current) uploadFileInputRef.current.value = "";
  };

  const handleFileSelect = (file: File) => {
    resetUploadState();
    if (!validateFile(file)) return;
    setSelectedFile(file);
    processFile(file);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === "dragenter" || e.type === "dragover");
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) handleFileSelect(e.dataTransfer.files[0]);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) handleFileSelect(e.target.files[0]);
  };

  const handleRemoveFile = () => {
    // Tell the parent to actually drop this audience from the campaign's
    // selection too — resetUploadState alone only cleared this tab's own
    // view, leaving the audience id (and its chip elsewhere) still selected.
    if (uploadedAudienceId) onRemoveAudience?.(uploadedAudienceId);
    resetUploadState();
  };

  // Re-submits the already-picked file through the same parse+upload path —
  // functionally identical to a fresh upload, just under a "Validate" label
  // to match Manual Entry's "Re-validate Contacts" affordance for the same
  // idea (re-run validation against the current file).
  const handleRevalidate = () => {
    if (selectedFile) processFile(selectedFile);
  };

  // ====================== UPLOAD TO SERVER ======================
  const uploadFileImmediately = async (
    file: File,
    parsedData?: { contacts: string[]; fileName: string; headers?: string[]; firstRow?: Record<string, string> }
  ) => {
    if (uploadInProgress.current || !file) return;

    uploadInProgress.current = true;
    setIsProcessing(true);
    setUploadProgress(0);
    setUploadError(null);

    const progressInterval = setInterval(() => {
      // Leave room (cap at 60%) for the polling phase below to keep
      // advancing the bar, rather than sitting at 90% for up to a minute.
      setUploadProgress((p) => (p >= 60 ? 60 : p + 10));
    }, 120);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const [response] = await uploadFileAudience(formData);

      clearInterval(progressInterval);

      const immediateId = extractAudienceId(response);
      const jobId = extractJobId(response);

      if (immediateId) {
        // Some deployments return the final result synchronously.
        setUploadProgress(100);
        finalizeUploadSuccess(unwrapPayload(response), file, parsedData);
      } else if (jobId) {
        // Async job accepted — the real result (audience_id + counts) isn't
        // known yet, it has to be polled for. Not a failure. The 1-row
        // preview (csv_headers/first_row_values) only comes back on THIS
        // response though — the status endpoint's completed payload doesn't
        // repeat them — so carry them forward to attach at finalize time.
        setUploadProgress(60);
        await pollUploadStatus(jobId, file, parsedData, {
          csv_headers: response?.csv_headers,
          first_row_values: response?.first_row_values,
        });
      } else {
        setUploadError(response?.detail || response?.message || "Failed to upload file to server");
      }
    } catch (error) {

      toast.error("Upload failed");
      setUploadError("Failed to upload file to server");
      onValidation(false);
      setUploadResults(null); // Clear any pending results
    } finally {
      setIsProcessing(false);
      setIsParsing(false);
      uploadInProgress.current = false;
    }
  };

  // Shared by both the synchronous-response path and the end of polling —
  // turns a final response (with audience_id/id + metadata) into UI state.
  const finalizeUploadSuccess = (
    data: any,
    file: File,
    parsedData?: { contacts: string[]; fileName: string; headers?: string[]; firstRow?: Record<string, string> }
  ) => {
    data = unwrapPayload(data);
    // The sync endpoint nests counts under `metadata`; the async job's
    // `completed` status has them flat on the job object instead (and uses
    // singular field names — `invalid_count`/`duplicate_count` — where the
    // sync response uses `invalids_count`/`duplicates_count`). Read both.
    const meta = data?.metadata || data || {};

    const valid = meta.unique_count ?? 0;
    const duplicates = meta.duplicates_count ?? meta.duplicate_count ?? 0;
    const invalids = meta.invalids_count ?? meta.invalid_count ?? 0;
    const unsubscribed = meta.unsubscribed_count ?? 0;
    const total = meta.total_count ?? meta.total_rows ?? (valid + duplicates + invalids);

    const results: UploadResults = {
      fileName: file.name,
      totalRecords: total,
      validRecords: valid,
      invalidRecords: invalids,
      duplicateRecords: duplicates,
      unsubscribedRecords: unsubscribed,
      csvColumns: parsedData?.headers || [],
      csvPreviewRow: parsedData?.firstRow || {},
      preview: parsedData?.contacts.slice(0, 5).map((contact, index) => ({
        sno: index + 1,
        contact,
        status: "valid",
      })) || [],
    };

    setUploadResults(results);
    setApiResponseData(data);

    const audienceId = data.audience_id || data.id;
    setUploadedAudienceId(audienceId);
    setHasUploaded(true);

    if (data.csv_headers && Array.isArray(data.csv_headers)) {
      localStorage.setItem("csv_headers", JSON.stringify(data.csv_headers));
    }
    if (data.first_row_values && Array.isArray(data.first_row_values)) {
      localStorage.setItem("first_row_values", JSON.stringify(data.first_row_values));
    }
    if (data.metadata && typeof data.metadata === "object" && data.metadata !== null) {
      localStorage.setItem("metadata", JSON.stringify(data.metadata));
    }
    toast.success(`Upload successful! ${valid} valid contacts`);
    onUploadSuccess(
      audienceId,
      file.name,
      valid,
      data.csv_headers,
      data.first_row_values,
      data.metadata,
      parsedData?.firstRow
    );
    onValidation(valid > 0);
  };

  // Polls GET /audience/upload/status/{job_id} until the job finishes,
  // fails, or we give up after UPLOAD_STATUS_MAX_ATTEMPTS — capped and
  // spaced out deliberately rather than retrying tightly or forever.
  const pollUploadStatus = async (
    jobId: string,
    file: File,
    parsedData?: { contacts: string[]; fileName: string; headers?: string[]; firstRow?: Record<string, string> },
    initialPreview?: { csv_headers?: string[]; first_row_values?: any[] }
  ) => {
    for (let attempt = 0; attempt < UPLOAD_STATUS_MAX_ATTEMPTS; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, UPLOAD_STATUS_POLL_INTERVAL_MS));

      let statusResponse: any;
      try {
        const [res] = await checkUploadStatus(undefined, undefined, jobId);
        statusResponse = res;
      } catch {
        continue; // transient network hiccup — try again next tick
      }

      // Job record expired (>24h) or never existed — not a processing
      // failure, just nothing left to poll. Stop instead of spinning for
      // the full timeout window.
      if (/job not found/i.test(statusResponse?.detail || "")) {
        setUploadError("This upload is no longer trackable — refresh the page; if it finished, it'll show up in the audience list.");
        return;
      }

      const state = extractStatusState(statusResponse);
      const resultId = extractAudienceId(statusResponse);
      const isFailed = FAILED_STATES.includes(state) || !!unwrapPayload(statusResponse)?.error;

      if (process.env.NODE_ENV !== "production") {
        // Deliberately verbose: this poll loop has already been the source
        // of one hard-to-diagnose "stuck spinner" bug because the raw
        // response shape didn't match what the code expected. If that
        // happens again, this is what to paste back rather than a screenshot.
        // eslint-disable-next-line no-console
        console.debug("[uploadTab] status poll", {
          attempt: attempt + 1,
          jobId,
          state,
          resultId,
          raw: statusResponse,
        });
      }

      if (isFailed) {
        const failedPayload = unwrapPayload(statusResponse);
        setUploadError(failedPayload?.error || failedPayload?.detail || failedPayload?.message || "Upload processing failed");
        return;
      }

      if (resultId || DONE_STATES.includes(state)) {
        setUploadProgress(100);
        const finalPayload = {
          ...unwrapPayload(statusResponse),
          csv_headers: initialPreview?.csv_headers,
          first_row_values: initialPreview?.first_row_values,
        };
        finalizeUploadSuccess(finalPayload, file, parsedData);
        return;
      }

      // Still queued/processing — keep polling, nudge the bar a little so
      // it doesn't look stuck.
      setUploadProgress((p) => Math.min(95, p + 2));
    }

    setUploadError("Upload is taking longer than expected — check back shortly or refresh.");
  };

  const isExistingAudience = !!audienceById &&
    audienceById.type !== "manual" &&
    (audienceById?.total_count ?? 0) > 0;

  const showExistingOnly = isExistingAudience && !selectedFile;

  const shouldShowResults = (apiResponseData || hasUploaded) && uploadResults;

  const downloadSampleCSV = () => {
    const sample = `email,first_name,last_name,Phone_Number,country,state,city,age,gender,dob
remember@fmac.ng,Kola,Bola, +91 9909878754,India,Dehil,Deoli, Early Career (25-34),Male,
fmac@example.com,Fola,Hola,+91 7098798755,,,,,Female,`;

    const blob = new Blob([sample], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "contacts-upload-template.csv";

    document.body.appendChild(a);
    a.click();

    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Excludes the upload tab's own audience — that one already renders in the
  // file-info card below with its own Delete button; showing it twice here
  // too would be redundant/confusing.
  const otherSelectedAudiences = (selectedAudiences || []).filter((a) => a.id !== uploadedAudienceId);

  return (
    <div className="space-y-4">
      {/* Audiences already selected from the other tabs (Segments/Manual) —
          they all feed the same campaign-wide selection, so switching to
          Upload shouldn't hide what's already picked. */}
      {otherSelectedAudiences.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {otherSelectedAudiences.map((a) => (
            <span
              key={a.id}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 pl-3 pr-2 py-1.5 text-sm"
            >
              <span className="font-medium text-gray-800 dark:text-gray-200">{a.name}</span>
              {onRemoveAudience && (
                <button
                  type="button"
                  onClick={() => onRemoveAudience(a.id)}
                  className="text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 leading-none bg-transparent border-none cursor-pointer"
                >
                  ×
                </button>
              )}
              <span className="ml-1 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                {a.contacts?.toLocaleString()} Estimated Reach
              </span>
            </span>
          ))}
        </div>
      )}

      {/* Upload Area */}
      <div
        className={`border-2 border-dashed flex flex-col justify-center items-center rounded-2xl px-6 py-6 bg-[#F3F8F6] dark:bg-teal-500/5 text-center transition-all ${dragActive ? "border-teal-500 bg-teal-50 dark:bg-teal-500/10" : "border-gray-300 dark:border-gray-600"
          }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >

        <p className="text-teal-600 dark:text-teal-400 font-medium mb-4">
          {showExistingOnly ? "Upload a new file or continue with existing" : "Upload your completed customer contact list"}
        </p>
        <button
          onClick={() => uploadFileInputRef.current?.click()}
          className="bg-white dark:bg-gray-800 w-fit my-3 hover:bg-gray-50 dark:hover:bg-gray-700 shadow text-gray-800 dark:text-gray-200 px-6 py-3 rounded-lg font-medium transition"
          type="button"
        >
          {showExistingOnly ? "Upload New File" : "Browse Files"}
        </button>
        <button
          onClick={downloadSampleCSV}
          type="button"
          className="flex items-center font-semibold gap-2 text-xs sm:text-sm text-[#4338CA] dark:text-indigo-400 underline hover:text-[#2855eb] dark:hover:text-indigo-300 transition-colors"
        >
          Download CSV Template <FileDown className="w-3 h-3 sm:w-4 sm:h-4" />
        </button>
        <p className="text-gray-500 dark:text-gray-400 text-sm mt-4">
          Supports CSV or XLSX • Upto 1M contacts • One contact per row
        </p>
        <input
          ref={uploadFileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleFileChange}
          className="hidden"
        />

      </div>



      {/* Progress - Show during parsing or processing */}
      {(isParsing || isProcessing) && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5">
          <div className="flex items-center mb-3">
            <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-2" />
            <p className="font-medium text-gray-900 dark:text-gray-100">
              {isParsing ? "Parsing file..." : "Uploading to server..."}
            </p>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 h-2 rounded-full overflow-hidden">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all"
              style={{ width: isParsing ? "60%" : `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* File Info - Only show after successful API response or existing audience */}
      {shouldShowResults && uploadResults && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 shrink-0" />
              <div className="min-w-0">
                <p className="font-medium text-[#4338CA] dark:text-indigo-400 underline truncate">
                  {uploadResults.fileName}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {uploadResults.validRecords.toLocaleString()} Contacts
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={handleRemoveFile}
                className="h-8 px-3.5 border border-gray-200 dark:border-gray-700 rounded-lg text-xs font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Delete
              </button>
              <button
                type="button"
                onClick={handleRevalidate}
                disabled={isProcessing || isParsing}
                className="h-8 px-3.5 rounded-lg text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isProcessing || isParsing ? "Validating..." : "Validate"}
              </button>
            </div>
          </div>

          <div className="flex items-center gap-0 text-xs text-gray-500 dark:text-gray-400 flex-wrap mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
            <span>
              Total <strong className="text-gray-700 dark:text-gray-300">{uploadResults.totalRecords.toLocaleString()}</strong> / 1M Numbers
            </span>
            <span className="mx-2 text-gray-300 dark:text-gray-600">|</span>
            <span className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
              Valid <strong className="text-green-600 dark:text-green-400 ml-0.5">{uploadResults.validRecords.toLocaleString()}</strong>
            </span>
            <span className="mx-2 text-gray-300 dark:text-gray-600">|</span>
            <span className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 inline-block" />
              Invalid <strong className="text-red-600 dark:text-red-400 ml-0.5">{uploadResults.invalidRecords.toLocaleString()}</strong>
            </span>
            <span className="mx-2 text-gray-300 dark:text-gray-600">|</span>
            <span className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500 inline-block" />
              Duplicates <strong className="text-blue-600 dark:text-blue-400 ml-0.5">{uploadResults.duplicateRecords.toLocaleString()}</strong>
            </span>
            {channel?.toLowerCase() === "email" && uploadResults.unsubscribedRecords > 0 && (
              <>
                <span className="mx-2 text-gray-300 dark:text-gray-600">|</span>
                <span className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-500 inline-block" />
                  Unsubscribed <strong className="text-orange-600 dark:text-orange-400 ml-0.5">{uploadResults.unsubscribedRecords.toLocaleString()}</strong>
                </span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Error - Show if upload failed */}
      {uploadError && (
        <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-center gap-3">
          <XCircle className="w-5 h-5 text-red-500 dark:text-red-400" />
          <p className="text-red-700 dark:text-red-400">{uploadError}</p>
        </div>
      )}

      {/* Manual Upload Button - Only show during processing state */}
      {!shouldShowResults && selectedFile && !hasUploaded && isProcessing && (
        <div className="flex justify-center pt-4">
          <button
            disabled={true}
            className="bg-gradient-to-r from-green-600 to-emerald-600 opacity-50 text-white px-10 py-3.5 rounded-xl font-medium flex items-center gap-2 transition cursor-not-allowed"
          >
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            Uploading...
          </button>
        </div>
      )}
    </div>
  );
};

export default UploadTab;